import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronDown,
  MinusCircle,
  Search,
  Upload,
  X,
} from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { SegmentedTabs } from "@pervasivemind/bi-components";

import { DateRangePicker } from "./DateRangePicker";
import { groupFacetTags, parseFacetSelection, withFacetAttribute } from "./facet-tags";
import { DateRangeSlider, type DateSliderStep } from "./DateRangeSlider";
import { NumberRangeSlider } from "./NumberRangeSlider";
import type { FetchRangeHistogram } from "./number-range";
import { formatFilterValue, isLatestDay, isUploadedSkuList, resolveFilterLabel } from "./filter-format";
import { useLang } from "./i18n-binding";
import { filtersForTab } from "./filter-tabs";
import { resolveLocalized } from "./i18n-resolve";
import {
  AUTOCOMPLETE_MIN_CHARS,
  OPTION_ROW_PX,
  SEARCH_VISIBLE_THRESHOLD,
  useOptionSearch,
  VirtualOptionList,
} from "./option-list";
import type { DashboardFilter } from "./types";

/** Tailwind class concatenator — local copy so the shared filter bar
 *  doesn't depend on each app's ``@/lib/utils``. */
function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

// ── Phase C runtime widgets — JSON encoding helpers ─────

/**
 * Decode a JSON-encoded filter value, returning `null` on any
 * parse error. Used by range / multi_select / chip widgets so a
 * malformed default doesn't crash the bar.
 */
function safeParseJson<T>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

interface GenericFilterBarProps {
  filters: ReadonlyArray<DashboardFilter>;
  filterOptions: Record<string, string[]>;
  activeFilters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
  /**
   * When true the bar renders against the dark banner gradient with
   * white/10 controls (matches hp-portal's "embedded" mode).
   */
  embedded?: boolean;
  /**
   * Colour surface of an ``embedded`` bar. ``"dark"`` (default) is the
   * historical navy banner; ``"light"`` keeps the embedded layout (no
   * card of its own) with the neutral palette, for the white
   * ``ControlPanel`` of the nuevo look (iteración 2, 2026-10-24).
   */
  surface?: "dark" | "light";
  /**
   * ``"wrap"`` (default) — the historical horizontal flex-wrap row.
   * ``"stack"`` — one full-width filter per row, for the filter cajón's
   * vertical drawer. Additive: every existing caller omits it and keeps
   * the byte-identical wrap layout.
   */
  layout?: "wrap" | "stack" | "rows";
  /**
   * Show the built-in header (the "Filters" caption + "Clear all"). The
   * cajón supplies its own header/footer, so it turns this off. Default
   * true — existing callers are unchanged.
   */
  showHeader?: boolean;
  /**
   * DSA "Filtros guardados": opens the host's saved-filters drawer. When
   * given, the view toolbar's "Save view" placeholder becomes a real
   * button and an empty ``sku_list`` filter offers to upload a list
   * there. Absent (the builder preview), both stay inert.
   */
  onSaveView?: () => void;
  /**
   * The ``sku_list`` picker's "Upload a file…" row: the host opens its
   * saved filters ready to upload a list (the portal: a new saved filter).
   * Absent, the row falls back to ``onSaveView``; with neither (the
   * builder preview) the picker has no upload row.
   */
  onUploadSkuList?: () => void;
  /**
   * Histogram fetcher for ``range`` filters that declare a
   * ``histogram_table``. The host closes over its active filters (portal:
   * ``/filters/{key}/histogram``; builder: ``/preview/range-histogram``)
   * and must keep its identity stable between filter changes. Absent =
   * the range panels show no bars and no "N left".
   */
  fetchRangeHistogram?: FetchRangeHistogram;
  /**
   * The current dashboard tab (the resolved id ``DashboardBody``
   * renders — ``resolveActiveTabId``). Filters whose ``tabs`` do not
   * name it are not drawn; they still apply to the query. Undefined or
   * null = every filter (a dashboard without tabs, and every
   * pre-existing caller).
   */
  activeTab?: string | null;
  /**
   * Grupos de reportes, decision F2 B (2026-10-24): the group filters the
   * report on screen declares «no aplica». In the rows layout they stay
   * in place, disabled, dashed, with the value struck through and
   * «no aplica en {report}» beside them; nothing can change them from
   * this tab. Absent = every filter is live (every report outside a
   * group).
   */
  notApplicable?: { keys: ReadonlySet<string>; reportLabel: string } | null;
}

/**
 * Cascading filter row.
 *
 * Custom dropdown buttons replace native ``<select>`` so the option list
 * inherits the banner styling (white text on the gradient instead of
 * OS-rendered bright menus). Filters declared as ``autocomplete``
 * additionally surface a search input — handy for long lists like
 * Seller Storefront name.
 */
/** Per-filter-type layout. The historical descriptor is `default` —
 *  every type that existed before epic v3 resolves to it, so this
 *  refactor is byte-identical for them (pinned by test). */
const FILTER_LAYOUT: Record<
  string,
  { wrapper: string; showLabel: boolean; inlineLabel?: boolean }
> = {
  default: {
    wrapper: "min-w-[9rem] flex-1 basis-[9rem] space-y-0.5",
    showLabel: true,
  },
  // Tabs own their row, and their label sits INLINE to the left
  // instead of stacked above in the uppercase micro-caption — a tab
  // strip with a caption over it reads as two controls, not one.
  tabs: { wrapper: "w-full basis-full", showLabel: false, inlineLabel: true },
};

export function GenericFilterBar({
  filters,
  filterOptions,
  activeFilters,
  onFilterChange,
  onClear,
  embedded,
  surface = "dark",
  layout = "wrap",
  showHeader = true,
  onSaveView,
  onUploadSkuList,
  fetchRangeHistogram,
  activeTab,
  notApplicable,
}: GenericFilterBarProps): JSX.Element | null {
  const { t } = useTranslation();
  const locale = useLang();
  if (filters.length === 0) return null;

  // Colour branches read ``dark``; layout branches keep reading ``embedded``.
  const dark = Boolean(embedded) && surface !== "light";
  const stack = layout === "stack";
  // ``"rows"`` (DSA 1.6): filters sharing a ``group`` render on one
  // labelled row (the group as caption, uppercase, left), ungrouped ones
  // on a trailing unlabelled row, and a view toolbar on top (Profile ·
  // Save view — labelled "soon" until card 1.7 — and Clear).
  const rows = layout === "rows";
  // Per-tab visibility (DSA): ``visible`` is what this bar DRAWS and
  // summarises (missing-required notice, Clear all). The dependency
  // state of a control (``disabled`` below) still reads the full list —
  // a parent hidden on this tab is still its parent. With nothing to
  // draw, the banner bar vanishes; the rows layout keeps its view
  // toolbar, so Save view / Clear stay reachable from every tab.
  const visible = filtersForTab(filters, activeTab);
  if (visible.length === 0 && !rows) return null;

  // A «no aplica» filter is drawn but asks nothing of the reader: an
  // empty required one is not a missing value on this tab.
  const isNotApplicable = (f: DashboardFilter): boolean =>
    notApplicable?.keys.has(f.key) === true;
  const hasRequiredMissing = visible.some(
    (f) => f.required && !activeFilters[f.key] && !f.locked && !isNotApplicable(f),
  );
  const hasActive = visible.some(f => f.depends_on != null) || visible.some(
    (f) => !f.locked && !f.required && activeFilters[f.key],
  );

  const containerClass = embedded
    ? dark
      ? "border-t border-white/15 mt-2"
      : stack
        ? "border-t border-neutral-200 mt-2"
        : ""
    : stack
      ? ""
      : "rounded-xl border border-neutral-200 bg-white shadow-sm";

  // Stack: one full-width filter per row (the cajón drawer). Wrap: the
  // historical horizontal flex-wrap row (the banner).
  const rowClass = stack
    ? "flex flex-col gap-3 px-1 py-1"
    : "flex flex-wrap items-end gap-2 px-3 py-2";

  // Rows layout: group filters by their resolved ``group`` caption, in
  // order of first appearance; the ungrouped remainder closes the list.
  const filterRows: Array<{ caption: string; filters: DashboardFilter[] }> = [];
  if (rows) {
    const byCaption = new Map<string, DashboardFilter[]>();
    for (const filt of visible) {
      const caption = resolveLocalized(filt.group ?? null, locale);
      const list = byCaption.get(caption);
      if (list) list.push(filt);
      else byCaption.set(caption, [filt]);
    }
    for (const [caption, list] of byCaption) {
      if (caption !== "") filterRows.push({ caption, filters: list });
    }
    const rest = byCaption.get("");
    if (rest) filterRows.push({ caption: "", filters: rest });
  }
  // One filter → one control. Shared by the three layouts; `rowSiblings`
  // is how many filters share the row (rows layout only).
  const renderFilter = (filt: DashboardFilter, rowSiblings = 1): JSX.Element => {
          const ftype = filt.type ?? "select";
          // Authored options win for curated multi_select/chip controls.
          // A multi_select without them is dynamic and receives its live,
          // sibling-filtered choices from the cascade response.
          const options =
            (ftype === "multi_select" || ftype === "chip") && filt.options?.length
              ? filt.options
              : (filterOptions[filt.key] ?? []);
          const value = activeFilters[filt.key] ?? "";
          // ``option_labels``: the value stays what the SQL sees; the
          // picker / tabs / chip show the (bilingual) label.
          const labelFor = filt.option_labels && Object.keys(filt.option_labels).length > 0
            ? (opt: string): string =>
                resolveLocalized(filt.option_labels?.[opt] ?? null, locale) || opt
            : undefined;
          // F2 B: a «no aplica» filter stays in place but cannot change.
          const na = isNotApplicable(filt);
          const disabled = na || Boolean(filt.locked) || Boolean(filt.depends_on?.some((key) =>
            filters.some(parent => parent.key === key && parent.required && !parent.locked && !activeFilters[key])
          ));
          // A facet_tags multi_select is a section of its own: the filter's
          // label as title, one picker per attribute (facet-tags.ts).
          if (ftype === "multi_select" && filt.facet_tags) {
            return (
              <FacetSection
                key={filt.key}
                filterKey={filt.key}
                title={resolveFilterLabel(filt, t, locale)}
                tags={options}
                value={value}
                dark={dark}
                disabled={disabled}
                pickerWrapper={stack ? "w-full space-y-0.5" : FILTER_LAYOUT.default!.wrapper}
                onChange={(v) => onFilterChange(filt.key, v)}
              />
            );
          }
          // Per-TYPE layout (epic v3). Every pre-existing type keeps the
          // historical wrapper byte-for-byte; `tabs` is the first one
          // that needs the full row and no micro-label — a tab strip
          // squeezed into a 9rem column with an uppercase caption above
          // it stops reading as a tab strip.
          const perType = FILTER_LAYOUT[ftype] ?? FILTER_LAYOUT.default!;
          // Stack mode (the cajón drawer) is a single vertical column:
          // EVERY filter gets the same stacked-above label — including
          // ``tabs``, whose inline-left label is a banner-only affordance.
          // Without forcing showLabel/inlineLabel here, a tabs filter kept
          // its 11px label beside the pills while its neighbours had the
          // 10px caption above → the "Category" mis-alignment.
          // Rows: the row caption already names a lone control, so the
          // filter's own label only shows when the row holds several.
          const layout = stack
            ? { wrapper: "w-full space-y-0.5", showLabel: true, inlineLabel: false }
            : rows
              ? {
                  wrapper: ftype === "tabs" ? "min-w-0 space-y-0.5" : "min-w-[9rem] space-y-0.5",
                  showLabel: rowSiblings > 1,
                  inlineLabel: false,
                }
              : perType;
          // Phase C runtime dispatch — one widget per filter type.
          const control = (
              ftype === "sku_list" ? (
                // A list uploaded with a saved filter is a reference the
                // host expands: a chip with its count and ×. Otherwise the
                // codes are picked like any multi-select (JP, 2026-10-26),
                // with "Upload a file…" inside the dropdown.
                isUploadedSkuList(value) ? (
                  <SkuListControl
                    value={value}
                    embedded={dark}
                    locked={Boolean(filt.locked) || na}
                    onClear={() => onFilterChange(filt.key, "")}
                  />
                ) : (
                  <MultiPicker
                    value={value}
                    options={options}
                    onChange={(v) => onFilterChange(filt.key, v)}
                    embedded={dark}
                    disabled={disabled}
                    footer={
                      !disabled && (onUploadSkuList ?? onSaveView)
                        ? { label: t("filterbar.skuListUpload"), onClick: (onUploadSkuList ?? onSaveView)! }
                        : undefined
                    }
                  />
                )
              ) : ftype === "date_slider" ? (
                <DateRangeSlider
                  value={value}
                  // Engine serves the column's global [min, max] ISO days
                  // through the same /filter-options payload the cascade
                  // types use.
                  bounds={filterOptions[filt.key] ?? []}
                  // ``?? "day"`` mirrors the engine's read-side parse
                  // default; publish-time validation guarantees the
                  // serialized value is one of day/week/month.
                  step={(filt.slider_step ?? "day") as DateSliderStep}
                  presets={filt.presets ?? undefined}
                  disabled={disabled}
                  embedded={dark}
                  onChange={(v) => onFilterChange(filt.key, v)}
                />
              ) : ftype === "date_range" || ftype === "date_relative" ? (
                <DateRangePicker
                  value={value}
                  disabled={disabled}
                  onChange={(v) => onFilterChange(filt.key, v)}
                  placeholder={t("daterange.placeholder")}
                  embedded={dark}
                  // Stack layout = the cajón drawer: portal the 520px
                  // calendar panel so it escapes the 360px scroll clip.
                  portal={stack}
                  hasTime={(filt.column_type ?? "")
                    .toUpperCase()
                    .startsWith("TIMESTAMP")}
                  configPresets={filt.presets ?? undefined}
                  // PR4 — the engine serves the column's global
                  // [min, max] day bounds for every date-window type;
                  // with them the picker popover embeds a dual-handle
                  // slider row. Absent (older cached payloads) → the
                  // popover renders exactly as before.
                  bounds={filterOptions[filt.key] ?? undefined}
                  // Snap granularity from the filter config; "week"
                  // when unset — the weekly gating reports are the
                  // primary consumer of the in-popover slider.
                  sliderStep={(filt.slider_step ?? "week") as DateSliderStep}
                />
              ) : ftype === "date_calendar" ? (
                <DayPicker
                  // ``"latest"`` (the report default) shows the max day
                  // the engine serves; the report SQL resolves the same.
                  value={
                    isLatestDay(value) ? (filterOptions[filt.key]?.[1] ?? "") : value
                  }
                  // The engine serves the column's [min, max] days: the
                  // picker cannot land on a day with no data. Absent or
                  // [] (older cached payloads, empty table) → unbounded.
                  min={filterOptions[filt.key]?.[0] || undefined}
                  max={filterOptions[filt.key]?.[1] || undefined}
                  disabled={disabled}
                  embedded={dark}
                  label={resolveFilterLabel(filt, t, locale)}
                  placeholder={t("filterbar.dayPlaceholder")}
                  onChange={(v) => onFilterChange(filt.key, v)}
                />
              ) : ftype === "search" ? (
                <SearchInput
                  value={value}
                  onChange={(v) => onFilterChange(filt.key, v)}
                  placeholder={t("filterbar.searchPlaceholder")}
                  embedded={dark}
                  disabled={disabled}
                />
              ) : ftype === "range" ? (
                <NumberRangeSlider
                  value={value}
                  onChange={(v) => onFilterChange(filt.key, v)}
                  // Engine serves the column's GLOBAL [min, max] as
                  // numeric strings through /filter-options, same
                  // payload as the date-window bounds.
                  bounds={filterOptions[filt.key] ?? []}
                  format={filt.value_format}
                  scale={filt.slider_scale}
                  label={resolveFilterLabel(filt, t, locale)}
                  triggerClassName={triggerClass(dark, disabled)}
                  fetchHistogram={
                    fetchRangeHistogram && filt.histogram_table
                      ? (edges, signal) => fetchRangeHistogram(filt.key, edges, signal)
                      : undefined
                  }
                  embedded={dark}
                  disabled={disabled}
                />
              ) : ftype === "tabs" ? (
                // Banner (wrap): label INLINE-left beside the pills. Drawer
                // (stack): the stacked-above caption already rendered it, so
                // the pills take the full row on their own line — same shape
                // and label size as every other filter in the column.
                <div
                  className={
                    stack
                      ? "min-w-0"
                      : "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2"
                  }
                >
                  {!stack && !rows && (
                    <span
                      className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide ${
                        dark ? "text-white/80" : "text-neutral-500"
                      }`}
                    >
                      {resolveFilterLabel(filt, t, locale)}
                    </span>
                  )}
                  <SegmentedTabs
                    // Look v3: the track hugs its options (it has a surface now,
                    // so stretching it would draw an empty grey bar).
                    className={stack ? "max-w-full" : "min-w-0"}
                    // `disabled` (locked / required parent empty): the UI
                    // is what stops a locked control emitting a value.
                    options={options.map((opt) => ({
                      value: String(opt),
                      label: labelFor?.(String(opt)) ?? String(opt),
                      disabled,
                    }))}
                    value={filt.locked ?? (value || null)}
                    onChange={(v) => onFilterChange(filt.key, v)}
                    shape="pill"
                    fill="inline"
                    size="sm"
                    tone={dark ? "accent" : "inset"}
                    ariaLabel={resolveFilterLabel(filt, t, locale)}
                  />
                </div>
              ) : ftype === "multi_select" || ftype === "chip" ? (
                <MultiPicker
                  value={value}
                  options={options}
                  onChange={(v) => onFilterChange(filt.key, v)}
                  embedded={dark}
                  disabled={disabled}
                  labelFor={labelFor}
                />
              ) : (
                (() => {
                  const Picker =
                    ftype === "autocomplete" ? AutocompletePicker : SelectPicker;
                  return (
                    <Picker
                      value={filt.locked ?? value}
                      disabled={disabled}
                      options={filt.locked ? [filt.locked] : options}
                      onChange={(v) => onFilterChange(filt.key, v)}
                      placeholder={
                        filt.required ? t("filterbar.pick") : t("filterbar.all")
                      }
                      allowEmpty={!filt.required}
                      embedded={dark}
                      labelFor={labelFor}
                    />
                  );
                })()
              )
          );
          return (
            <div
              key={filt.key}
              data-not-applicable={na ? filt.key : undefined}
              // Dashed outline + the chosen value struck through (the
              // selected pill, or the picker's trigger text): shape and
              // text, never colour alone, say it does not apply here.
              className={cn(
                layout.wrapper,
                na && "rounded-lg border border-dashed px-2 py-1",
                na && (dark ? "border-white/40" : "border-neutral-400"),
                na && "[&_[aria-checked=true]]:line-through [&_button:not([role=radio])]:line-through",
              )}
            >
              {layout.showLabel && (
              <label
                className={`block text-2xs font-semibold uppercase tracking-wide ${
                  dark ? "text-white/80" : "text-neutral-600"
                }`}
              >
                {resolveFilterLabel(filt, t, locale)}
                {filt.required && (
                  <span className="ml-0.5 text-red-400">*</span>
                )}
                {filt.locked && (
                  <span
                    className={`ml-1 rounded px-1 text-2xs uppercase tracking-wider ${
                      dark
                        ? "bg-white/10 text-white/75"
                        : "bg-neutral-200 text-neutral-600"
                    }`}
                  >
                    {t("filterbar.locked")}
                  </span>
                )}
              </label>
              )}
              {na ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {control}
                  <span
                    className={`inline-flex items-center gap-1 text-xs not-italic no-underline ${
                      dark ? "text-white/80" : "text-neutral-600"
                    }`}
                  >
                    <MinusCircle size={12} aria-hidden />
                    {t("filters.notApplicableIn", { report: notApplicable?.reportLabel ?? "" })}
                  </span>
                </div>
              ) : (
                control
              )}
            </div>
          );
  };
  return (
    <div className={containerClass}>
      {rows && (
        <ViewToolbar
          embedded={dark}
          hasActive={hasActive}
          onClear={onClear}
          onSaveView={onSaveView}
        />
      )}
      {rows ? (
        <div className="px-3 pb-2" data-filter-rows="">
          {filterRows.map((row) => (
            <div
              key={row.caption || "__rest__"}
              data-filter-row={row.caption || undefined}
              className={`flex flex-wrap items-end gap-x-3 gap-y-2 border-t py-2 ${
                dark ? "border-white/10" : "border-neutral-100"
              }`}
            >
              {row.caption !== "" && (
                <span
                  className={`w-24 shrink-0 self-center text-2xs font-extrabold uppercase tracking-widest ${
                    dark ? "text-white/75" : "text-neutral-500"
                  }`}
                >
                  {row.caption}
                </span>
              )}
              {row.filters.map((filt) => renderFilter(filt, row.filters.length))}
            </div>
          ))}
        </div>
      ) : (
      <div className={rowClass}>
        {showHeader && (
        <div className="flex w-full items-center justify-between pb-0.5">
          <span
            className={`text-2xs font-extrabold uppercase tracking-widest ${
              dark ? "text-white/75" : "text-neutral-800"
            }`}
          >
            {t("filterbar.title")}
          </span>
          {/* Anti-jump (§2.5): "Clear all" ALWAYS occupies its slot in
              the flow — it fades to invisible (non-interactive) when no
              filter is active instead of unmounting, so the banner's
              header row never changes height/width when the first
              filter is applied or the last one cleared. */}
          <button
            type="button"
            onClick={onClear}
            disabled={!hasActive}
            aria-hidden={!hasActive}
            tabIndex={hasActive ? 0 : -1}
            className={`text-xs font-medium underline underline-offset-2 transition-opacity ${
              hasActive ? "" : "invisible pointer-events-none"
            } ${
              dark
                ? "text-white/80 hover:text-white"
                : "text-primary hover:text-primary-ink"
            }`}
          >
            {t("filterbar.clearAll")}
          </button>
        </div>
        )}

        {visible.map((filt) => renderFilter(filt))}
      </div>
      )}

      {hasRequiredMissing && (
        <div
          className={`flex items-center gap-2 border-t px-4 py-2 ${
            dark
              ? "border-red-400/30 bg-red-500/10"
              : "border-red-200 bg-red-50"
          }`}
        >
          <AlertTriangle size={14} className="shrink-0 text-red-400" />
          <span
            className={`text-xs font-medium ${
              dark ? "text-red-300" : "text-error-dark"
            }`}
          >
            {t("filterbar.requiredHint")}
          </span>
        </div>
      )}
    </div>
  );
}


// ── View toolbar (rows layout, DSA 1.6) ──────────────────
// Profile and "Save view" are card 1.7 (saved views): rendered as
// labelled stubs — visible, disabled, tagged "soon" — rather than
// omitted, so the bar reads like the mockup and nobody wires a fake.
/**
 * DSA "Filtros guardados" — a ``sku_list`` value that is a list UPLOADED
 * with a saved filter (a reference the host expands, or its expansion):
 * a chip that says how many codes narrow the report (``filter-format``
 * decides the text) with a clear button. Codes picked by hand use the
 * multi-select picker instead.
 */
function SkuListControl({
  value,
  embedded,
  locked,
  onClear,
}: {
  value: string;
  embedded: boolean;
  locked: boolean;
  onClear: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const locale = useLang();
  const text = formatFilterValue({ type: "sku_list" }, value, { t, locale });
  return (
    <span
      className={`inline-flex min-h-[2rem] items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium ${
        embedded
          ? "border-accent/50 bg-accent/25 text-white"
          : "border-accent/40 bg-accent/10 text-accent-dark"
      }`}
      data-testid="sku-list-chip"
    >
      <span>{text}</span>
      {!locked && (
        <button
          type="button"
          onClick={onClear}
          aria-label={t("filterbar.skuListClear")}
          className="rounded p-0.5 hover:bg-black/10"
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </span>
  );
}

function ViewToolbar({
  embedded,
  hasActive,
  onClear,
  onSaveView,
}: {
  embedded: boolean;
  hasActive: boolean;
  onClear: () => void;
  onSaveView?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const muted = embedded ? "text-white/60" : "text-neutral-400";
  const soon = (
    <span
      className={`ml-1 rounded px-1 text-2xs uppercase tracking-wider ${
        embedded ? "bg-white/10 text-white/70" : "bg-neutral-200 text-neutral-500"
      }`}
    >
      {t("filterbar.soon")}
    </span>
  );
  return (
    <div
      data-view-toolbar=""
      className="flex flex-wrap items-center justify-between gap-2 px-3 pt-2 pb-1"
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-2xs font-extrabold uppercase tracking-widest ${
            embedded ? "text-white/75" : "text-neutral-800"
          }`}
        >
          {t("filterbar.profile")}
        </span>
        <span aria-disabled="true" className={`text-xs ${muted}`}>
          —{soon}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {onSaveView !== undefined ? (
          <button
            type="button"
            onClick={onSaveView}
            className={`text-xs font-medium underline underline-offset-2 ${
              embedded ? "text-white/90 hover:text-white" : "text-primary hover:text-primary-ink"
            }`}
          >
            {t("filterbar.savedFilters")}
          </button>
        ) : (
          <span aria-disabled="true" className={`text-xs ${muted}`}>
            {t("filterbar.saveView")}
            {soon}
          </span>
        )}
        <button
          type="button"
          onClick={onClear}
          disabled={!hasActive}
          className={`text-xs font-medium underline underline-offset-2 transition-opacity ${
            hasActive ? "" : "opacity-40"
          } ${embedded ? "text-white/80 hover:text-white" : "text-primary hover:text-primary-ink"}`}
        >
          {t("filterbar.clear")}
        </button>
      </div>
    </div>
  );
}

// ── Shared dropdown primitive ────────────────────────────

interface PickerProps {
  value: string;
  disabled: boolean;
  options: ReadonlyArray<string>;
  /** Display label for an option VALUE (``option_labels``); absent =
   *  the value is its own label. */
  labelFor?: (opt: string) => string;
  onChange: (value: string) => void;
  placeholder: string;
  allowEmpty: boolean;
  embedded: boolean;
}

/**
 * Hint row for the large-list autocomplete tier — shown while the
 * min-chars gate holds the filter back (see ``useOptionSearch``). The
 * full universe stays rendered (virtualized) underneath: search is a
 * shortcut, never the only way to reach an option.
 */
/** The one-day picker (`date_calendar`). The native `<input type="date">`
 *  stays — it owns the calendar, the [min, max] bounds and keyboard
 *  entry — but it sits transparent over a trigger that looks like the
 *  other filters and prints the day as ISO `yyyy-mm-dd`, whatever the
 *  browser's locale (Chrome drew `dd-mm-aaaa` from the OS). A click
 *  anywhere opens the calendar: Chrome only opened it from the small
 *  icon, so a click in the middle did nothing visible. The empty state
 *  is a muted format hint, not a value-looking placeholder. */
function DayPicker({
  value,
  min,
  max,
  disabled,
  embedded,
  label,
  placeholder,
  onChange,
}: {
  value: string;
  min: string | undefined;
  max: string | undefined;
  disabled: boolean;
  embedded: boolean;
  label: string;
  placeholder: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const openPicker = (): void => {
    const el = inputRef.current;
    if (!el || disabled) return;
    // Feature check, not error swallowing: `showPicker` exists in every
    // browser the portal supports (Chrome 99+, Safari 16+, Firefox 101+);
    // where it does not, the transparent input still takes the click.
    if (typeof el.showPicker === "function") el.showPicker();
  };
  return (
    <div
      data-day-picker=""
      onClick={openPicker}
      className={cn(
        triggerClass(embedded, disabled),
        "relative focus-within:ring-2",
        embedded ? "focus-within:ring-white/40" : "focus-within:ring-accent/40",
        !disabled && "cursor-pointer",
      )}
    >
      <span
        data-day-picker-text=""
        className={cn(
          "min-w-0 truncate tabular-nums",
          value ? "" : embedded ? "font-normal text-white/45" : "font-normal text-neutral-500",
        )}
      >
        {value || placeholder}
      </span>
      <CalendarDays
        size={12}
        aria-hidden="true"
        className={cn("shrink-0", embedded ? "text-white/60" : "text-neutral-400")}
      />
      <input
        ref={inputRef}
        type="date"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        className={cn(
          "absolute inset-0 h-full w-full opacity-0",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      />
    </div>
  );
}

function MinCharsHint({ embedded }: { embedded: boolean }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "border-b px-3 py-1.5 text-2xs",
        embedded
          ? "border-white/10 text-white/75"
          : "border-neutral-200 text-neutral-600",
      )}
    >
      {t("filterbar.typeToSearch", { min: AUTOCOMPLETE_MIN_CHARS })}
    </div>
  );
}

/**
 * Minimal styled select replacement. Renders a button + popover with
 * the same dark-on-banner palette so the menu feels native to the
 * dashboard's gradient.
 *
 * Long option lists (> ``SEARCH_VISIBLE_THRESHOLD``) DELEGATE to the
 * searchable ``AutocompletePicker`` — one implementation of search +
 * virtualization + keyboard + ARIA instead of a ~110-line fork. The
 * props contract is identical, so the swap is transparent. The
 * threshold is pure UX (when a search box is worth the space) — never
 * a bound on what is rendered or reachable: the delegated picker
 * virtualizes the FULL option universe. At or below the threshold the
 * plain dropdown renders in full, exactly as before.
 */
function SelectPicker(props: PickerProps): JSX.Element {
  if (props.options.length > SEARCH_VISIBLE_THRESHOLD) {
    return <AutocompletePicker {...props} />;
  }
  return <PlainSelectPicker {...props} />;
}

/** The at-or-below-cap plain dropdown (no search box, no highlight). */
function PlainSelectPicker({
  value,
  disabled,
  options,
  onChange,
  placeholder,
  allowEmpty,
  embedded,
  labelFor,
}: PickerProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            // Swallow Escape: it must close ONLY this dropdown, not bubble
            // up to a host modal's document-level Escape handler (the
            // PreviewModal closes on Escape).
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
        className={triggerClass(embedded, disabled)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
      >
        {/* Anti-jump (§2.5): the selected value renders truncated inside
            the trigger's flex-determined width — a long value NEVER
            widens the control (which would re-wrap the bar and change
            the banner height). */}
        <span
          className={cn(
            "min-w-0 truncate",
            value ? "" : embedded ? "text-white/75" : "text-neutral-500",
          )}
        >
          {value ? (labelFor?.(value) ?? value) : placeholder}
        </span>
        <ChevronDown
          size={12}
          className={cn("shrink-0", embedded ? "text-white/60" : "text-neutral-400")}
        />
      </button>
      {open && (
        <Popover embedded={embedded} id={listboxId} role="listbox">
          {allowEmpty && (
            <PopoverItem
              embedded={embedded}
              active={value === ""}
              role="option"
              ariaSelected={value === ""}
              onSelect={() => {
                onChange("");
                setOpen(false);
              }}
            >
              {placeholder}
            </PopoverItem>
          )}
          {options.map((opt) => (
            <PopoverItem
              key={opt}
              embedded={embedded}
              active={opt === value}
              role="option"
              ariaSelected={opt === value}
              onSelect={() => {
                onChange(opt);
                setOpen(false);
              }}
            >
              {labelFor?.(opt) ?? opt}
            </PopoverItem>
          ))}
          {options.length === 0 && (
            <div
              className={cn(
                "px-3 py-2 text-xs",
                embedded ? "text-white/75" : "text-neutral-600",
              )}
            >
              {t("filterbar.noOptions")}
            </div>
          )}
        </Popover>
      )}
    </div>
  );
}

/**
 * Searchable dropdown for autocomplete filters (and the delegated
 * long-list ``select`` case).
 *
 * Mirrors hp-portal's SellerAutocomplete behaviour: the popover shows a
 * search input that filters the option list, plus arrow-key + Enter
 * keyboard navigation. The list is VIRTUALIZED (never sliced): the
 * scrollbar spans the complete option universe and scrolling alone
 * reaches every option; the search box is a convenience. Above
 * ``LARGE_LIST_THRESHOLD`` the filter waits for the min-chars gate +
 * debounce (``useOptionSearch``) — the universe stays scrollable while
 * a hint row invites narrowing.
 *
 * Keyboard model:
 *   - highlight starts at -1 (nothing highlighted): Enter right after
 *     opening only closes the popover — it never commits an option the
 *     user did not pick;
 *   - an APPLIED query highlights the first match (so query + Enter
 *     commits it) — keyed off ``effectiveQuery``, not the raw text, so
 *     in the debounced tier Enter always commits something visible;
 *   - the "All" row (empty value), when visible, occupies navigation
 *     slot 0 so it is keyboard-reachable like any option;
 *   - ArrowUp/Down auto-scroll the virtual window to keep the
 *     highlighted row mounted (which keeps ``aria-activedescendant``
 *     pointing at a real DOM id);
 *   - the highlight clamps whenever the list shrinks under it (async
 *     option reloads, narrowing queries);
 *   - Escape closes ONLY the popover (swallowed so a host modal's own
 *     Escape handler doesn't fire).
 *
 * Row hover feedback is pure CSS (no per-row mouse-enter state — a
 * mouse sweep across the list must not re-render it); the ``highlight``
 * state is keyboard-only. That also keeps the virtual window stable
 * under the pointer: only scroll and keyboard move it.
 */
function AutocompletePicker({
  value,
  disabled,
  options,
  onChange,
  placeholder,
  allowEmpty,
  embedded,
}: PickerProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  const { query, setQuery, filtered, effectiveQuery, hintMinChars } =
    useOptionSearch(options, open);

  // Navigation index space: the "All" row (when rendered) occupies slot
  // 0; option ``i`` sits at ``i + navOffset``. -1 = nothing highlighted.
  const showAll = allowEmpty && !query;
  const navOffset = showAll ? 1 : 0;
  const navCount = filtered.length + navOffset;

  useEffect(() => {
    if (open) {
      setHighlight(-1);
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open]);

  // An APPLIED filter highlights its first match; clearing it clears the
  // highlight. Keyed off effectiveQuery (not the raw text) so the
  // highlight only lands once the user can SEE the filtered list.
  useEffect(() => {
    setHighlight(effectiveQuery ? 0 : -1);
  }, [effectiveQuery]);

  // Clamp when the option list shrinks below the highlight while the
  // popover is open (async options arriving, or a narrowing query).
  useEffect(() => {
    setHighlight((h) => (h >= navCount ? navCount - 1 : h));
  }, [navCount]);

  const commitSlot = (slot: number): void => {
    if (showAll && slot === 0) {
      onChange("");
      setOpen(false);
      return;
    }
    const pick = filtered[slot - navOffset];
    if (pick === undefined) return;
    onChange(pick);
    setOpen(false);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(navCount - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(-1, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Only a highlight the user reached (arrows or typing) commits;
      // Enter with no highlight just closes.
      if (highlight >= 0) commitSlot(highlight);
      else setOpen(false);
    } else if (e.key === "Escape") {
      // Close ONLY the dropdown — without stopPropagation the event
      // reaches the PreviewModal's document-level handler and closes the
      // whole modal along with it.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const activeDescendant =
    open && highlight >= 0 ? `${listboxId}-opt-${highlight}` : undefined;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={triggerClass(embedded, disabled)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
      >
        {/* Anti-jump (§2.5): truncated value — see PlainSelectPicker. */}
        <span
          className={cn(
            "min-w-0 truncate",
            value ? "" : embedded ? "text-white/75" : "text-neutral-500",
          )}
        >
          {value || placeholder}
        </span>
        <ChevronDown
          size={12}
          className={cn("shrink-0", embedded ? "text-white/60" : "text-neutral-400")}
        />
      </button>
      {open && (
        <Popover
          embedded={embedded}
          id={listboxId}
          role="listbox"
          ariaLabel={placeholder}
          scrollable={false}
        >
          <div
            className={cn(
              "flex items-center gap-1.5 border-b px-2 py-1.5",
              embedded ? "border-white/10" : "border-neutral-200",
            )}
          >
            <Search
              size={12}
              className={embedded ? "text-white/40" : "text-neutral-400"}
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t("filterbar.searchOptions")}
              aria-label={t("filterbar.searchOptions")}
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-activedescendant={activeDescendant}
              aria-autocomplete="list"
              className={cn(
                "flex-1 bg-transparent text-xs outline-none",
                embedded ? "text-white placeholder-white/75" : "text-neutral-700",
              )}
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className={cn(
                  "rounded p-0.5",
                  embedded
                    ? "text-white/40 hover:text-white"
                    : "text-neutral-400 hover:text-neutral-700",
                )}
                title={t("filterbar.clear")}
              >
                <X size={12} />
              </button>
            )}
          </div>
          {hintMinChars && <MinCharsHint embedded={embedded} />}
          <VirtualOptionList
            count={navCount}
            followIndex={highlight}
            renderRow={(slot) => {
              if (showAll && slot === 0) {
                return (
                  <div key="__all__" style={{ height: OPTION_ROW_PX }}>
                    <PopoverItem
                      embedded={embedded}
                      active={value === ""}
                      highlighted={highlight === 0}
                      id={`${listboxId}-opt-0`}
                      role="option"
                      ariaSelected={value === ""}
                      onSelect={() => {
                        onChange("");
                        setOpen(false);
                      }}
                    >
                      {placeholder}
                    </PopoverItem>
                  </div>
                );
              }
              const opt = filtered[slot - navOffset]!;
              return (
                <div key={opt} style={{ height: OPTION_ROW_PX }}>
                  <PopoverItem
                    embedded={embedded}
                    active={opt === value}
                    highlighted={slot === highlight}
                    id={`${listboxId}-opt-${slot}`}
                    role="option"
                    ariaSelected={opt === value}
                    onSelect={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    {opt}
                  </PopoverItem>
                </div>
              );
            }}
          />
          {filtered.length === 0 && (
            <div
              className={cn(
                "px-3 py-2 text-xs",
                embedded ? "text-white/75" : "text-neutral-600",
              )}
            >
              {t("filterbar.noMatches")}
            </div>
          )}
        </Popover>
      )}
    </div>
  );
}

// ── UI primitives ────────────────────────────────────────
// Exported so the DateRangePicker (and any future filter picker)
// can reuse the same trigger button, popover chrome, and
// outside-click behavior without duplicating the embedded/light-mode
// palette.

export function triggerClass(embedded: boolean, disabled: boolean): string {
  return cn(
    "flex h-7 w-full items-center justify-between gap-1 rounded-lg border px-2 text-xs font-medium outline-none transition-colors",
    embedded
      ? "border-white/20 bg-white/10 text-white hover:bg-white/15 focus:border-white/40"
      : "border-control bg-white text-neutral-800 hover:bg-neutral-50 focus:border-primary focus:ring-2 focus:ring-primary/20",
    disabled && "cursor-not-allowed opacity-60",
  );
}

export function Popover({
  embedded,
  children,
  className,
  id,
  role,
  ariaLabel,
  scrollable = true,
}: {
  embedded: boolean;
  children: React.ReactNode;
  /** Optional extra classes — the DateRangePicker needs a wider panel. */
  className?: string;
  /** ARIA listbox plumbing (combobox pattern) — optional so non-listbox
   * panels (date pickers) stay untouched. */
  id?: string;
  role?: string;
  ariaLabel?: string;
  /** ``false`` when a child owns its own scroll viewport (the
   * virtualized option list) — a scrolling panel around a scrolling
   * window would double the scrollbars. */
  scrollable?: boolean;
}): JSX.Element {
  return (
    <div
      id={id}
      role={role}
      aria-label={ariaLabel}
      className={cn(
        "absolute z-30 mt-1 w-full min-w-[10rem] rounded-xl shadow-menu",
        scrollable && "max-h-72 overflow-y-auto",
        embedded
          ? "border border-white/15 bg-navy text-white"
          : "border border-neutral-200 bg-white text-neutral-700",
        className,
      )}
    >
      {/* Cast through Fragment: React 19's ReactNode includes bigint
          which collides with react-i18next's stricter
          ReactI18NextChildren JSX child augmentation. Wrapping in
          <></> lets the union resolve to the looser parent type. */}
      <>{children}</>
    </div>
  );
}

export interface PopoverItemProps {
  embedded: boolean;
  active?: boolean;
  highlighted?: boolean;
  onSelect: () => void;
  onMouseEnter?: () => void;
  children: React.ReactNode;
  /** ARIA option plumbing (combobox pattern) — optional so existing
   * non-listbox consumers are untouched. ``id`` also anchors the
   * combobox input's ``aria-activedescendant``. */
  id?: string;
  role?: string;
  ariaSelected?: boolean;
}

export function PopoverItem({
  embedded,
  active,
  highlighted,
  onSelect,
  onMouseEnter,
  children,
  id,
  role,
  ariaSelected,
}: PopoverItemProps): JSX.Element {
  return (
    <button
      type="button"
      id={id}
      role={role}
      aria-selected={ariaSelected}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      className={cn(
        "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors",
        embedded
          ? highlighted
            ? "bg-white/15 text-white"
            : active
              ? "bg-white/10 text-white"
              : "text-white/85 hover:bg-white/10"
          : highlighted
            ? "bg-primary-soft text-primary-ink"
            : active
              ? "bg-primary-soft font-semibold text-primary-ink"
              : "text-neutral-700 hover:bg-primary-soft",
      )}
    >
      {/* Fragment wrap — see Popover above for the React 19 +
          react-i18next bigint/ReactNode mismatch. */}
      <span className="truncate"><>{children}</></span>
      {active && <Check size={12} className="shrink-0" />}
    </button>
  );
}

export function useDismissOnOutsideClick(
  // ``RefObject<HTMLElement | null>`` accommodates both
  // ``useRef<HTMLDivElement>(null)`` (React 19, current: T | null) and
  // ``useRef<HTMLElement>()`` (React 18 narrower default). All call
  // sites in this file create a div ref, but the hook only needs the
  // element's ``contains()`` method which both subtypes provide.
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onDismiss();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, active, onDismiss]);
}

// ── Phase C — runtime widgets for the new filter types ───

interface RuntimeFilterCommon {
  value: string;
  onChange: (raw: string) => void;
  embedded: boolean;
  disabled: boolean;
}

/** Free-text contains-style search. Plain string value. */
function SearchInput({
  value,
  onChange,
  placeholder,
  embedded,
  disabled,
}: RuntimeFilterCommon & { placeholder: string }): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs",
        embedded
          ? "border-white/15 bg-white/10 text-white"
          : "border-neutral-200 bg-white text-neutral-800",
        disabled && "opacity-50",
      )}
    >
      <Search size={12} className={embedded ? "text-white/50" : "text-neutral-400"} />
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full bg-transparent outline-none",
          embedded ? "placeholder:text-white/75" : "placeholder:text-neutral-500",
        )}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={() => onChange("")}
          className={embedded ? "text-white/50 hover:text-white" : "text-neutral-400 hover:text-neutral-600"}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * Multi-select picker. Encodes selection as JSON list of strings.
 * Compact dropdown with checkboxes; closes on outside click.
 *
 * High-cardinality lists get the same tiers as the single-select
 * pickers via the shared ``useOptionSearch`` hook: a search box above
 * ``SEARCH_VISIBLE_THRESHOLD``, min-chars gate + debounce above
 * ``LARGE_LIST_THRESHOLD``. The list itself is VIRTUALIZED — the
 * scrollbar spans every option at any cardinality, and the SELECTION is
 * independent of what is rendered (narrowing the query never unticks
 * anything).
 */
/**
 * A ``facet_tags`` filter drawn as a section: its label as the title, then
 * one multi-select per attribute listing that attribute's values — the
 * same picker as any multi_select. Every picker writes the ONE filter
 * value (the JSON list of "Attribute · Value" tags) through
 * ``withFacetAttribute``; the engine combines them (AND across
 * attributes, OR within one). An attribute the cascade no longer offers
 * keeps its picker while it has a selection, so it stays visible and
 * removable. No attribute at all (none for this category) → no section.
 */
function FacetSection({
  filterKey,
  title,
  tags,
  value,
  dark,
  disabled,
  pickerWrapper,
  onChange,
}: {
  filterKey: string;
  title: string;
  tags: ReadonlyArray<string>;
  value: string;
  /** Colour surface (the bar's ``dark``: embedded on the dark banner). */
  dark: boolean;
  disabled: boolean;
  pickerWrapper: string;
  onChange: (value: string) => void;
}): JSX.Element | null {
  const offered = useMemo(() => groupFacetTags(tags), [tags]);
  const picked = useMemo(() => groupFacetTags(parseFacetSelection(value)), [value]);
  const groups = useMemo(() => {
    const known = new Set(offered.map((g) => g.attribute));
    return [...offered, ...picked.filter((g) => !known.has(g.attribute))];
  }, [offered, picked]);
  if (groups.length === 0) return null;
  const pickedBy = new Map(picked.map((g) => [g.attribute, g.values]));
  return (
    <div
      className="w-full basis-full"
      role="group"
      aria-label={title}
      data-facet-section={filterKey}
    >
      <div
        className={`mb-1 border-t pt-2 text-2xs font-extrabold uppercase tracking-widest ${
          dark ? "border-white/15 text-white/85" : "border-neutral-200 text-neutral-700"
        }`}
      >
        {title}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {groups.map((group) => {
          const selected = pickedBy.get(group.attribute) ?? [];
          return (
            <div key={group.attribute} className={pickerWrapper} data-facet-attribute={group.attribute}>
              <label
                className={`block text-2xs font-semibold uppercase tracking-wide ${
                  dark ? "text-white/80" : "text-neutral-600"
                }`}
              >
                {group.attribute}
              </label>
              <MultiPicker
                value={selected.length === 0 ? "" : JSON.stringify(selected)}
                options={group.values}
                onChange={(v) => onChange(withFacetAttribute(value, group.attribute, parseFacetSelection(v)))}
                embedded={dark}
                disabled={disabled}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MultiPicker({
  value,
  options,
  onChange,
  embedded,
  disabled,
  labelFor,
  footer,
}: RuntimeFilterCommon & {
  options: ReadonlyArray<string>;
  labelFor?: (opt: string) => string;
  /** One action pinned under the options (the SKU list's "Upload a
   *  file…"): closes the dropdown and hands over to the host. */
  footer?: { label: string; onClick: () => void };
}): JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  useDismissOnOutsideClick(ref, open, () => setOpen(false));

  const searchable = options.length > SEARCH_VISIBLE_THRESHOLD;
  const { query, setQuery, filtered, hintMinChars } = useOptionSearch(
    options,
    open,
  );

  useEffect(() => {
    if (open && searchable) {
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open, searchable]);

  const selected = useMemo<Set<string>>(() => {
    const arr = safeParseJson<string[]>(value);
    return new Set(Array.isArray(arr) ? arr : []);
  }, [value]);

  const toggle = (opt: string): void => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    if (next.size === 0) onChange("");
    else onChange(JSON.stringify(Array.from(next)));
  };

  const summary =
    selected.size === 0
      ? t("filterbar.all")
      : selected.size === 1
        ? (labelFor?.(Array.from(selected)[0]!) ?? Array.from(selected)[0]!)
        : t("filterbar.nSelected", { n: selected.size });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center justify-between gap-1 rounded-md border px-2 py-1.5 text-left text-xs transition-colors",
          embedded
            ? "border-white/15 bg-white/10 text-white hover:bg-white/15"
            : "border-neutral-200 bg-white text-neutral-800 hover:bg-neutral-50",
          disabled && "opacity-50",
        )}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown size={12} className={embedded ? "text-white/50" : "text-neutral-400"} />
      </button>
      {open && (
        <div
          className={cn(
            "absolute z-20 mt-1 min-w-full rounded-xl border py-1 shadow-menu",
            embedded
              ? "border-white/15 bg-slate-900 text-white"
              : "border-neutral-200 bg-white text-neutral-800",
          )}
        >
          {searchable && (
            <div
              className={cn(
                "flex items-center gap-1.5 border-b px-2 py-1.5",
                embedded ? "border-white/10" : "border-neutral-200",
              )}
            >
              <Search
                size={12}
                className={embedded ? "text-white/40" : "text-neutral-400"}
              />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    // Close ONLY this dropdown — never a host modal.
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(false);
                  }
                }}
                placeholder={t("filterbar.searchOptions")}
                aria-label={t("filterbar.searchOptions")}
                className={cn(
                  "flex-1 bg-transparent text-xs outline-none",
                  embedded
                    ? "text-white placeholder-white/75"
                    : "text-neutral-700",
                )}
              />
            </div>
          )}
          {hintMinChars && searchable && <MinCharsHint embedded={embedded} />}
          {options.length === 0 ? (
            <div
              className={cn(
                "px-2 py-1 text-xs",
                embedded ? "text-white/75" : "text-neutral-600",
              )}
            >
              {t("filterbar.noOptions")}
            </div>
          ) : (
            <>
              <VirtualOptionList
                count={filtered.length}
                renderRow={(i) => {
                  const opt = filtered[i]!;
                  const isOn = selected.has(opt);
                  return (
                    <div key={opt} style={{ height: OPTION_ROW_PX }}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isOn}
                        onClick={() => toggle(opt)}
                        className={cn(
                          "flex h-full w-full items-center gap-2 px-2 text-left text-xs",
                          embedded ? "hover:bg-white/10" : "hover:bg-neutral-50",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          style={{ borderRadius: 3 }}
                          className={cn(
                            "grid h-3.5 w-3.5 shrink-0 place-items-center rounded border",
                            isOn
                              ? "border-primary bg-primary text-white"
                              : embedded
                                ? "border-white/30"
                                : "border-neutral-300",
                          )}
                        >
                          {isOn && <Check size={10} />}
                        </span>
                        <span className="truncate">{labelFor?.(opt) ?? opt}</span>
                      </button>
                    </div>
                  );
                }}
              />
              {searchable && filtered.length === 0 && (
                <div
                  className={cn(
                    "px-3 py-2 text-xs",
                    embedded ? "text-white/75" : "text-neutral-600",
                  )}
                >
                  {t("filterbar.noMatches")}
                </div>
              )}
            </>
          )}
          {footer && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                footer.onClick();
              }}
              className={cn(
                "mt-1 flex w-full items-center gap-2 border-t px-2 pt-2 pb-1 text-left text-xs font-semibold",
                embedded
                  ? "border-white/10 text-white hover:bg-white/10"
                  : "border-neutral-200 text-primary-ink hover:bg-neutral-50",
              )}
              data-testid="multi-picker-footer"
            >
              <Upload size={12} aria-hidden />
              {footer.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
